// PWPOL lane — password policy admin + self-service (backend djangoapp/urls_password_policy.py).
import Axios from 'axios';

const bp = '/bp';

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem('Token')}`,
  'Content-Type': 'application/json',
  Accept: 'application/json'
});

const unwrap = (p) =>
  p
    .then((r) => r.data)
    .catch((e) => e?.response?.data || { status: 'failed', message: e?.message || 'Network error' });

export const isOk = (r) => r?.status === true || r?.status === 'success' || r?.ok === true;

export const passwordPolicyApi = {
  // administration
  list: () => unwrap(Axios.get(`${bp}/password-policy/`, { headers: headers() })),
  save: (body) => unwrap(Axios.post(`${bp}/password-policy/`, body, { headers: headers() })),
  remove: (id, change_reason) =>
    unwrap(Axios.delete(`${bp}/password-policy/${id}/`, { headers: headers(), data: { change_reason } })),
  affectedUsers: (id) => unwrap(Axios.get(`${bp}/password-policy/${id}/users/`, { headers: headers() })),
  effective: (userId) =>
    unwrap(
      Axios.get(`${bp}/password-policy/effective/`, {
        headers: headers(),
        params: userId ? { user_id: userId } : {}
      })
    ),
  // self service
  status: () => unwrap(Axios.get(`${bp}/password/status/`, { headers: headers() })),
  change: (body) => unwrap(Axios.post(`${bp}/password/change/`, body, { headers: headers() }))
};

/** The rule list the server computed for a policy — used by the change-password modal. */
export const describeRules = (policy) => {
  if (!policy) return [];
  const out = [`At least ${policy.min_length} characters`];
  if (policy.min_classes) out.push(`At least ${policy.min_classes} of: uppercase, lowercase, digit, symbol`);
  if (policy.require_upper) out.push('Must contain an uppercase letter');
  if (policy.require_lower) out.push('Must contain a lowercase letter');
  if (policy.require_digit) out.push('Must contain a digit');
  if (policy.require_symbol) out.push('Must contain a symbol');
  if (policy.history_count) out.push(`Cannot reuse your last ${policy.history_count} passwords`);
  if (policy.reuse_username_blocked) out.push('Cannot contain your username or e-mail name');
  if (policy.expiry_days) out.push(`Expires every ${policy.expiry_days} days`);
  return out;
};

/** Client-side mirror of the server rules, so the modal can validate as the user types. */
export const checkPassword = (password, policy, username = '') => {
  const pw = password || '';
  const p = policy || {};
  const classes = {
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw)
  };
  const classCount = Object.values(classes).filter(Boolean).length;
  const checks = [{ label: `At least ${p.min_length || 12} characters`, ok: pw.length >= (p.min_length || 12) }];
  if (p.min_classes) {
    checks.push({
      label: `At least ${p.min_classes} of: uppercase, lowercase, digit, symbol`,
      ok: classCount >= p.min_classes
    });
  }
  if (p.require_upper) checks.push({ label: 'An uppercase letter', ok: classes.upper });
  if (p.require_lower) checks.push({ label: 'A lowercase letter', ok: classes.lower });
  if (p.require_digit) checks.push({ label: 'A digit', ok: classes.digit });
  if (p.require_symbol) checks.push({ label: 'A symbol', ok: classes.symbol });
  if (p.reuse_username_blocked && username) {
    const name = String(username).toLowerCase();
    const local = name.includes('@') ? name.split('@')[0] : name;
    checks.push({
      label: 'Does not contain your username',
      ok: !(local.length >= 3 && pw.toLowerCase().includes(local))
    });
  }
  if (p.history_count) checks.push({ label: `Not one of your last ${p.history_count} passwords`, ok: null });
  return checks;
};

export default passwordPolicyApi;
